import React, { useState } from 'react';
import { Link } from 'react-router-dom';

// Center links. External marketing pages open on godoorly.com in a new tab;
// "Sign in" routes to the in-app /login.
const NAV_LINKS = [
  { label: 'How it works', href: 'https://www.godoorly.com/how-it-works', external: true },
  { label: 'Why Doorly', href: 'https://www.godoorly.com/why-doorly', external: true },
  { label: 'Calculator', href: 'https://www.godoorly.com/calculator', external: true },
  { label: 'Sign in', href: '/login' },
];

function LandingNav() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [loginHover, setLoginHover] = useState(false);

  return (
    <header
      className="sticky top-0 z-50"
      style={{
        background: 'rgba(255, 255, 255, 0.78)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
        // iOS: the WebView extends behind the status bar (overlaysWebView), so
        // pad the header itself by the safe-area inset — its frosted background
        // fills the status-bar strip and the logo/menu sit below it instead of
        // starting behind the clock/battery.
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}
    >
      {/* 3-column grid: links are centered to the full page width (1fr auto 1fr),
          not just the space between logo and buttons. On mobile the center
          column has no content (nav is hidden) so it collapses — logo stays
          flush left, hamburger flush right, exactly as before. */}
      <div
        className="mx-auto py-4 px-8 md:px-12"
        style={{
          maxWidth: 1200,
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
        }}
      >
        {/* Left: Doorly logo — flush left */}
        <div style={{ gridColumn: '1', display: 'flex', justifyContent: 'flex-start' }}>
          <Link to="/" className="flex items-center" style={{ textDecoration: 'none' }}>
            <img
              src="/doorly_logo_black_icon.png"
              alt="Doorly"
              style={{ height: 32, width: 'auto', display: 'block' }}
            />
          </Link>
        </div>

        {/* Center: nav links — truly page-centered (hidden on mobile) */}
        <nav className="hidden md:flex items-center" style={{ gap: 36, fontSize: 14, fontWeight: 500 }} aria-label="Primary">
          {NAV_LINKS.map((link) =>
            link.external ? (
              <a
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: '14px', fontWeight: 500, color: '#000000', textDecoration: 'none' }}
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.label}
                to={link.href}
                style={{ fontSize: '14px', fontWeight: 500, color: '#000000', textDecoration: 'none' }}
              >
                {link.label}
              </Link>
            )
          )}
        </nav>

        {/* Right: Get Started + Log In (desktop) and hamburger (mobile) — flush right.
            Pinned to grid column 3 so that when the center nav is display:none on
            mobile (removed from grid flow), this cell stays right-aligned instead of
            auto-placing into the empty middle column. */}
        <div style={{ gridColumn: '3', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
          <div className="hidden md:flex items-center" style={{ gap: 10 }}>
            <Link to="/pre-qualify" className="doorly-cta">
              <span className="doorly-cta-text">Get Started</span>
              <span className="doorly-cta-badge" aria-hidden="true">→</span>
            </Link>
            {/* Log In — outline pill with blue circle + yellow arrow (mirrors the
                DoorlyCTA badge; arrow tilts -45° on hover). */}
            <Link
              to="/login"
              onMouseEnter={() => setLoginHover(true)}
              onMouseLeave={() => setLoginHover(false)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
                padding: '6px 6px 6px 18px',
                fontSize: 14,
                fontWeight: 600,
                color: '#000000',
                textDecoration: 'none',
                border: '1px solid rgba(0,0,0,0.14)',
                borderRadius: 999,
                background: '#fff',
                letterSpacing: '-0.005em',
              }}
            >
              <span>Log In</span>
              <span style={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                background: '#3a6ad9',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'transform 300ms cubic-bezier(.34,1.56,.64,1)',
                transform: loginHover ? 'rotate(-45deg)' : 'rotate(0deg)',
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fffd5b" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M5 12h14" />
                  <path d="M13 5l7 7-7 7" />
                </svg>
              </span>
            </Link>
          </div>

          {/* Mobile menu button (< md) */}
          <button
            type="button"
            className="md:hidden p-2 -mr-2 text-black"
            onClick={() => setMenuOpen((open) => !open)}
            aria-label="Toggle navigation menu"
            aria-expanded={menuOpen}
          >
            <div className="w-6 h-6 flex flex-col justify-center gap-1.5">
              <span className={`block h-0.5 bg-current transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
              <span className={`block h-0.5 bg-current transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`} />
              <span className={`block h-0.5 bg-current transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
            </div>
          </button>
        </div>
      </div>

      {/* Mobile dropdown menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-300 ${menuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
        style={{ borderTop: menuOpen ? '1px solid rgba(0, 0, 0, 0.06)' : 'none', background: 'rgba(255, 255, 255, 0.95)' }}
      >
        <div className="flex flex-col gap-1" style={{ padding: '12px 20px 20px' }}>
          {NAV_LINKS.filter((link) => link.href !== '/login').map((link) =>
            link.external ? (
              <a
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMenuOpen(false)}
                className="py-2.5"
                style={{ fontSize: '15px', fontWeight: 500, color: '#000000', textDecoration: 'none' }}
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.label}
                to={link.href}
                onClick={() => setMenuOpen(false)}
                className="py-2.5"
                style={{ fontSize: '15px', fontWeight: 500, color: '#000000', textDecoration: 'none' }}
              >
                {link.label}
              </Link>
            )
          )}
          <Link
            to="/pre-qualify"
            onClick={() => setMenuOpen(false)}
            className="doorly-cta doorly-cta-full"
            style={{ marginTop: '8px' }}
          >
            <span className="doorly-cta-text">Get Started</span>
            <span className="doorly-cta-badge" aria-hidden="true">→</span>
          </Link>
          {/* 🔑 Sign In in the menu is the Get Started pill with no fill (Jesse, 2026-10-19); the text link is dropped above. */}
          <Link
            to="/login"
            onClick={() => setMenuOpen(false)}
            className="doorly-cta doorly-cta-ghost doorly-cta-full"
            style={{ marginTop: '8px' }}
          >
            <span className="doorly-cta-text">Sign In</span>
            <span className="doorly-cta-badge" aria-hidden="true">→</span>
          </Link>
        </div>
      </div>
    </header>
  );
}

export default LandingNav;
